import * as React from 'react';
import notify from '../../../../../shared/feedback/notifications';
import { connect } from 'react-redux';
import {
    Button,
    ButtonGroup,
    ButtonToolbar,
    Col,
    ControlLabel,
    FormControl,
    FormGroup,
    OverlayTrigger,
    Panel,
    Row,
    Tooltip
} from 'react-bootstrap';
import * as actions from '../actions/PlanOrderActions';
import * as agentActions from './Actions';
import * as Features from '../../../../../shared/Features';
import { assign } from '../../../../../shared/Assign';
import { Translate } from '../../../../../shared/LanguageService';
import { IAnnotationViewModel } from '../Entities';
import * as FileExtensions from '../../../../../components/common/planOrder/FileExtensions';
import { IExternalFile, IFile, IUploadPlanOrderStoreyAnnotationFileResult } from './Entities';
import { AnnotationNormal, ExternalAnnotation } from '../../../../planannotation/AnnotationTypes';
import { handleError } from '../../../../../shared/ErrorCatching';
import ImageModal from '../../../../../components/common/planOrder/ImageModal';
import PlanAnnotationTool from '../../../../planannotation/PlanAnnotationTool';
import ConfirmationModal from '../../../../../components/common/ConfirmationModal';
import AnnotationFilesUploadModal from './AnnotationFilesUploadModal';
import StoreyFile from './StoreyFile';

import ExternalImageDeliveryFile from '../../../../../components/common/planOrder/externalFiles/ExternalImageFile';
import unknownIcon from '../../../../../assets/images/mystery-file-icon.svg';
import pdfIcon from '../../../../../assets/images/pdf-icon.svg';
import skpIcon from '../../../../../assets/images/skp-icon.svg';
import psdIcon from '../../../../../assets/images/psd-icon.svg';

interface IStoreyFile extends IFile {
    id: number;
    thumbnailReference: string;
    isAnnotation: boolean;
    deliveryFileId: number;
}

interface IStorey {
    id: number;

    storey: string;
    customStoreyName: string;

    sketchUpPlan2Ds: IStoreyFile[];
    sketchUpPlan3Ds: IStoreyFile[];
    roomSketcherPlan2Ds: IStoreyFile[];
    roomSketcherPlan3Ds: IStoreyFile[];
}

interface IPlanOrder {
    id: number;
    stories: IStorey[];
    externalDeliveryFiles: IExternalFile[];
    externalAnnotationFiles: IExternalFile[];
    orderApiUrl: string;
    orderCollectionId: number;
    contentProviderValue: number;
    addressLine1: string;
}

interface IActions {
    downloadFiles: (planOrderId: number) => Promise<any>;
    emailFiles: (planOrderId: number) => Promise<any>;
    return: (planOrderId: number, customerReturnedMessage: string) => Promise<any>;
    editAnnotation: (id: number) => Promise<any>;
    editExternalAnnotation: (id: number) => Promise<any>;
    saveDeliveryAnnotation: (
        planOrderStoreyFileId: number,
        data: string,
        revisionType: number,
        annotationType: string,
        hasDoneCrop: boolean,
        hasDoneFlip: boolean
    ) => Promise<any>;
    addPlanOrderStoreyAnnotationFile: (
        response: IUploadPlanOrderStoreyAnnotationFileResult
    ) => Promise<any>;
    addPlanOrderAnnotationFile: (response: IExternalFile) => Promise<any>;
    inActivateExternalFile: (id: number) => Promise<any>;
}

interface IProps {
    planOrder: IPlanOrder;
    actions: IActions;
    annotation: IAnnotationViewModel;
    availableStories: any[];
    roomSketcherFurnishLink: any;
    user: any;
}

interface IState {
    showImageModal: boolean;
    selectedImage: IFile;
    showReturnConfirmation: boolean;
    showAnnotationModal: boolean;
    customerReturnedMessage: string;
    showUploadModal: boolean;
    uploadExternalAnnotationFiles: boolean;
    annotationType: string;

    storiesUploads: {
        [id: number]: {
            blueprint: boolean;
            external: boolean;
            sketchUp2D: boolean;
            showSketchUp2D: boolean;
            sketchUp3D: boolean;
            showSketchUp3D: boolean;
            roomSketcher2D: boolean;
            showRoomSketcher2D: boolean;
            roomSketcher3D: boolean;
            showRoomSketcher3D: boolean;
        };
    };
}

class DeliveredPlanOrder extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            showImageModal: false,
            selectedImage: null,
            showAnnotationModal: false,
            showReturnConfirmation: false,
            customerReturnedMessage: null,
            showUploadModal: false,
            uploadExternalAnnotationFiles: false,
            annotationType: null,
            storiesUploads: {}
        };

        this.downloadFiles = this.downloadFiles.bind(this);
        this.emailFiles = this.emailFiles.bind(this);
        this.return = this.return.bind(this);
        this.uploadStoreyFile = this.uploadStoreyFile.bind(this);
        this.inActivateExternalFile = this.inActivateExternalFile.bind(this);
    }

    uploadStoreyFile(deliveryFileId: number, property: string) {
        let storiesUploads = assign({}, this.state.storiesUploads);

        if (!storiesUploads[deliveryFileId]) {
            storiesUploads[deliveryFileId] = {};
        }

        storiesUploads[deliveryFileId][property] = true;
        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStoreyFilesUploadCompleted(deliveryFileId: number, ...property: string[]) {
        let storiesUploads = assign({}, this.state.storiesUploads);

        property.forEach((x) => {
            storiesUploads[deliveryFileId][x] = false;
        });

        this.setState({ storiesUploads: storiesUploads } as IState);
    }

    onStoreyAnnotationFileUploadCompleted(
        response: any,
        action: (response: IUploadPlanOrderStoreyAnnotationFileResult) => Promise<any>
    ) {
        response = response.xhr.response;

        if (response) {
            let res = JSON.parse(response);
            let data: IUploadPlanOrderStoreyAnnotationFileResult = {
                storeyId: res.planOrderStoreyId,
                storeyFileId: res.planOrderStoreyFileId,
                fileReference: res.fileReference,
                thumbnailReference: res.thumbnailReference,
                existingStoreyFileId: res.existingStoreyFileId,
                isAnnotation: res.isAnnotation,
                filename: res.filename
            };

            action(data);
        }
    }

    onAnnotationFileUploadCompleted(
        response: any,
        action: (response: IExternalFile) => Promise<any>
    ) {
        response = response.xhr.response;

        if (response) {
            let res = JSON.parse(response);
            res.files.forEach((file) => {
                let data = {
                    planOrderExternalFileId: file.id,
                    filename: file.filename,
                    fileReference: file.fileReference,
                    fileType: file.fileType,
                    thumbnailReference: file.thumbnailReference,
                    oldFileId: file.oldFileId
                };

                action(data);
            });
        }
    }

    render() {
        const vitecBrokerSystemValue = 3;

        const { apiUrl } = window.appConfig;

        return (
            <div>
                {this.props.planOrder && (
                    <div>
                        {this.props.roomSketcherFurnishLink && (
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('order/plan/delivery/room-sketch-link')}
                                </ControlLabel>
                                <FormControl.Static>
                                    <a target="_blank" href={this.props.roomSketcherFurnishLink}>
                                        {this.props.roomSketcherFurnishLink}
                                    </a>
                                </FormControl.Static>
                            </FormGroup>
                        )}
                        {this.props.planOrder.contentProviderValue === vitecBrokerSystemValue && (
                            <Panel>
                                <Panel.Heading>
                                    <Panel.Title>
                                        {Translate('order/plan/delivery/vitec-broker-system/title')}
                                    </Panel.Title>
                                </Panel.Heading>
                                <Panel.Body>
                                    <a
                                        href={
                                            this.props.planOrder.orderApiUrl +
                                            '/deliver/vitecmaklarsystem?orderId=' +
                                            this.props.planOrder.id
                                        }
                                        target="_blank"
                                    >
                                        {Translate(
                                            'order/plan/delivery/vitec-broker-system/delivery-link'
                                        )}
                                    </a>
                                </Panel.Body>
                            </Panel>
                        )}
                        <div className="generated-plan-sketches">
                            <h2>{Translate('plan-order/delivery/generation/title')}</h2>
                            <Row>
                                {this.props.planOrder.stories.map((storey, index) => (
                                    <div
                                        key={'storey-' + index}
                                        className="plan-delivery-storey-files"
                                    >
                                        {storey.sketchUpPlan2Ds &&
                                            storey.sketchUpPlan2Ds.map((sketchUpPlan2D, index) => (
                                                <Col md={4} key={index}>
                                                    <StoreyFile
                                                        title={`${
                                                            storey.customStoreyName
                                                                ? storey.customStoreyName
                                                                : Translate(storey.storey)
                                                        } ${Translate(
                                                            'order/plan/delivery/2d-title'
                                                        )}`}
                                                        reference={sketchUpPlan2D.fileReference}
                                                        thumbnailReference={
                                                            sketchUpPlan2D.thumbnailReference
                                                        }
                                                        onClick={() =>
                                                            this.setState({
                                                                showImageModal: true,
                                                                selectedImage:
                                                                    sketchUpPlan2D as IFile
                                                            } as IState)
                                                        }
                                                        onAnnotate={() =>
                                                            this.showAnnotationModal(
                                                                sketchUpPlan2D.id
                                                            )
                                                        }
                                                        onShowUploadModal={() =>
                                                            this.uploadStoreyFile(
                                                                sketchUpPlan2D.deliveryFileId,
                                                                'showSketchUp2D'
                                                            )
                                                        }
                                                        showUploadModal={
                                                            this.state.storiesUploads[
                                                                sketchUpPlan2D.deliveryFileId
                                                            ]
                                                                ? this.state.storiesUploads[
                                                                      sketchUpPlan2D.deliveryFileId
                                                                  ].showSketchUp2D
                                                                : false
                                                        }
                                                        onUpload={() =>
                                                            this.uploadStoreyFile(
                                                                sketchUpPlan2D.deliveryFileId,
                                                                'sketchUp2D'
                                                            )
                                                        }
                                                        dropzoneId={
                                                            'storey-sketchup-2d-customer-' + index
                                                        }
                                                        uploadActionUrl={`${apiUrl}/views/planorder/agent/order/storey/${sketchUpPlan2D.id}`}
                                                        upload={
                                                            this.state.storiesUploads[
                                                                sketchUpPlan2D.deliveryFileId
                                                            ]
                                                                ? this.state.storiesUploads[
                                                                      sketchUpPlan2D.deliveryFileId
                                                                  ].sketchUp2D
                                                                : false
                                                        }
                                                        onFileUploadCompleted={(response: any) =>
                                                            this.onStoreyAnnotationFileUploadCompleted(
                                                                response,
                                                                this.props.actions
                                                                    .addPlanOrderStoreyAnnotationFile
                                                            )
                                                        }
                                                        onUploadCompleted={() =>
                                                            this.onStoreyFilesUploadCompleted(
                                                                sketchUpPlan2D.deliveryFileId,
                                                                'sketchUp2D',
                                                                'showSketchUp2D'
                                                            )
                                                        }
                                                        onUploadModalClose={() =>
                                                            this.onStoreyFilesUploadCompleted(
                                                                sketchUpPlan2D.deliveryFileId,
                                                                'showSketchUp2D'
                                                            )
                                                        }
                                                        isAnnotation={sketchUpPlan2D.isAnnotation}
                                                        uploadModalTitleSubject={
                                                            (storey.customStoreyName
                                                                ? storey.customStoreyName
                                                                : Translate(storey.storey)) +
                                                            ' (2D)'
                                                        }
                                                    />
                                                </Col>
                                            ))}
                                        {storey.sketchUpPlan3Ds &&
                                            storey.sketchUpPlan3Ds.map((sketchUpPlan3D, index) => (
                                                <Col md={4} key={index}>
                                                    <StoreyFile
                                                        title={`${
                                                            storey.customStoreyName
                                                                ? storey.customStoreyName
                                                                : Translate(storey.storey)
                                                        } ${Translate(
                                                            'order/plan/delivery/3d-title'
                                                        )}`}
                                                        reference={sketchUpPlan3D.fileReference}
                                                        thumbnailReference={
                                                            sketchUpPlan3D.thumbnailReference
                                                        }
                                                        onClick={() =>
                                                            this.setState({
                                                                showImageModal: true,
                                                                selectedImage:
                                                                    sketchUpPlan3D as IFile
                                                            } as IState)
                                                        }
                                                        onAnnotate={() =>
                                                            this.showAnnotationModal(
                                                                sketchUpPlan3D.id
                                                            )
                                                        }
                                                        onShowUploadModal={() =>
                                                            this.uploadStoreyFile(
                                                                sketchUpPlan3D.deliveryFileId,
                                                                'showSketchUp3D'
                                                            )
                                                        }
                                                        showUploadModal={
                                                            this.state.storiesUploads[
                                                                sketchUpPlan3D.deliveryFileId
                                                            ]
                                                                ? this.state.storiesUploads[
                                                                      sketchUpPlan3D.deliveryFileId
                                                                  ].showSketchUp3D
                                                                : false
                                                        }
                                                        onUpload={() =>
                                                            this.uploadStoreyFile(
                                                                sketchUpPlan3D.deliveryFileId,
                                                                'sketchUp3D'
                                                            )
                                                        }
                                                        dropzoneId={
                                                            'storey-sketchup-3d-customer-' + index
                                                        }
                                                        uploadActionUrl={`${apiUrl}/views/planorder/agent/order/storey/${sketchUpPlan3D.id}`}
                                                        upload={
                                                            this.state.storiesUploads[
                                                                sketchUpPlan3D.deliveryFileId
                                                            ]
                                                                ? this.state.storiesUploads[
                                                                      sketchUpPlan3D.deliveryFileId
                                                                  ].sketchUp3D
                                                                : false
                                                        }
                                                        onFileUploadCompleted={(response: any) =>
                                                            this.onStoreyAnnotationFileUploadCompleted(
                                                                response,
                                                                this.props.actions
                                                                    .addPlanOrderStoreyAnnotationFile
                                                            )
                                                        }
                                                        onUploadCompleted={() =>
                                                            this.onStoreyFilesUploadCompleted(
                                                                sketchUpPlan3D.deliveryFileId,
                                                                'sketchUp3D',
                                                                'showSketchUp3D'
                                                            )
                                                        }
                                                        onUploadModalClose={() =>
                                                            this.onStoreyFilesUploadCompleted(
                                                                sketchUpPlan3D.deliveryFileId,
                                                                'showSketchUp3D'
                                                            )
                                                        }
                                                        isAnnotation={sketchUpPlan3D.isAnnotation}
                                                        uploadModalTitleSubject={
                                                            (storey.customStoreyName
                                                                ? storey.customStoreyName
                                                                : Translate(storey.storey)) +
                                                            ' (3D)'
                                                        }
                                                    />
                                                </Col>
                                            ))}

                                        {storey.roomSketcherPlan2Ds &&
                                            storey.roomSketcherPlan2Ds.map(
                                                (roomSketcherPlan2D, index) => (
                                                    <Col md={4} key={index}>
                                                        <StoreyFile
                                                            title={`${
                                                                storey.customStoreyName
                                                                    ? storey.customStoreyName
                                                                    : Translate(storey.storey)
                                                            } ${Translate(
                                                                'order/plan/delivery/2d-title'
                                                            )}`}
                                                            reference={
                                                                roomSketcherPlan2D.fileReference
                                                            }
                                                            thumbnailReference={
                                                                roomSketcherPlan2D.thumbnailReference
                                                            }
                                                            onClick={() =>
                                                                this.setState({
                                                                    showImageModal: true,
                                                                    selectedImage:
                                                                        roomSketcherPlan2D as IFile
                                                                } as IState)
                                                            }
                                                            onAnnotate={() =>
                                                                this.showAnnotationModal(
                                                                    roomSketcherPlan2D.id
                                                                )
                                                            }
                                                            onShowUploadModal={() =>
                                                                this.uploadStoreyFile(
                                                                    roomSketcherPlan2D.deliveryFileId,
                                                                    'showRoomSketcher2D'
                                                                )
                                                            }
                                                            showUploadModal={
                                                                this.state.storiesUploads[
                                                                    roomSketcherPlan2D
                                                                        .deliveryFileId
                                                                ]
                                                                    ? this.state.storiesUploads[
                                                                          roomSketcherPlan2D
                                                                              .deliveryFileId
                                                                      ].showRoomSketcher2D
                                                                    : false
                                                            }
                                                            onUpload={() =>
                                                                this.uploadStoreyFile(
                                                                    roomSketcherPlan2D.deliveryFileId,
                                                                    'roomSketcher2D'
                                                                )
                                                            }
                                                            dropzoneId={
                                                                'storey-roomsketcher-2d-customer-' +
                                                                index
                                                            }
                                                            uploadActionUrl={`${apiUrl}/views/planorder/agent/order/storey/${roomSketcherPlan2D.id}`}
                                                            upload={
                                                                this.state.storiesUploads[
                                                                    roomSketcherPlan2D
                                                                        .deliveryFileId
                                                                ]
                                                                    ? this.state.storiesUploads[
                                                                          roomSketcherPlan2D
                                                                              .deliveryFileId
                                                                      ].roomSketcher2D
                                                                    : false
                                                            }
                                                            onFileUploadCompleted={(
                                                                response: any
                                                            ) =>
                                                                this.onStoreyAnnotationFileUploadCompleted(
                                                                    response,
                                                                    this.props.actions
                                                                        .addPlanOrderStoreyAnnotationFile
                                                                )
                                                            }
                                                            onUploadCompleted={() =>
                                                                this.onStoreyFilesUploadCompleted(
                                                                    roomSketcherPlan2D.deliveryFileId,
                                                                    'roomSketcher2D',
                                                                    'showRoomSketcher2D'
                                                                )
                                                            }
                                                            onUploadModalClose={() =>
                                                                this.onStoreyFilesUploadCompleted(
                                                                    roomSketcherPlan2D.deliveryFileId,
                                                                    'showRoomSketcher2D'
                                                                )
                                                            }
                                                            isAnnotation={
                                                                roomSketcherPlan2D.isAnnotation
                                                            }
                                                            uploadModalTitleSubject={
                                                                (storey.customStoreyName
                                                                    ? storey.customStoreyName
                                                                    : Translate(storey.storey)) +
                                                                ' (RoomSketcher 2D)'
                                                            }
                                                        />
                                                    </Col>
                                                )
                                            )}
                                        {storey.roomSketcherPlan3Ds &&
                                            storey.roomSketcherPlan3Ds.map(
                                                (roomSketcherPlan3D, index) => (
                                                    <Col md={4} key={index}>
                                                        <StoreyFile
                                                            title={`${
                                                                storey.customStoreyName
                                                                    ? storey.customStoreyName
                                                                    : Translate(storey.storey)
                                                            } ${Translate(
                                                                'order/plan/delivery/3d-title'
                                                            )}`}
                                                            reference={
                                                                roomSketcherPlan3D.fileReference
                                                            }
                                                            thumbnailReference={
                                                                roomSketcherPlan3D.thumbnailReference
                                                            }
                                                            onClick={() =>
                                                                this.setState({
                                                                    showImageModal: true,
                                                                    selectedImage:
                                                                        roomSketcherPlan3D as IFile
                                                                } as IState)
                                                            }
                                                            onAnnotate={() =>
                                                                this.showAnnotationModal(
                                                                    roomSketcherPlan3D.id
                                                                )
                                                            }
                                                            onShowUploadModal={() =>
                                                                this.uploadStoreyFile(
                                                                    roomSketcherPlan3D.deliveryFileId,
                                                                    'showRoomSketcher3D'
                                                                )
                                                            }
                                                            showUploadModal={
                                                                this.state.storiesUploads[
                                                                    roomSketcherPlan3D
                                                                        .deliveryFileId
                                                                ]
                                                                    ? this.state.storiesUploads[
                                                                          roomSketcherPlan3D
                                                                              .deliveryFileId
                                                                      ].showRoomSketcher3D
                                                                    : false
                                                            }
                                                            onUpload={() =>
                                                                this.uploadStoreyFile(
                                                                    roomSketcherPlan3D.deliveryFileId,
                                                                    'roomSketcher3D'
                                                                )
                                                            }
                                                            dropzoneId={
                                                                'storey-roomsketcher-3d-customer-' +
                                                                index
                                                            }
                                                            uploadActionUrl={`${apiUrl}/views/planorder/agent/order/storey/${roomSketcherPlan3D.id}`}
                                                            upload={
                                                                this.state.storiesUploads[
                                                                    roomSketcherPlan3D
                                                                        .deliveryFileId
                                                                ]
                                                                    ? this.state.storiesUploads[
                                                                          roomSketcherPlan3D
                                                                              .deliveryFileId
                                                                      ].roomSketcher3D
                                                                    : false
                                                            }
                                                            onFileUploadCompleted={(
                                                                response: any
                                                            ) =>
                                                                this.onStoreyAnnotationFileUploadCompleted(
                                                                    response,
                                                                    this.props.actions
                                                                        .addPlanOrderStoreyAnnotationFile
                                                                )
                                                            }
                                                            onUploadCompleted={() =>
                                                                this.onStoreyFilesUploadCompleted(
                                                                    roomSketcherPlan3D.deliveryFileId,
                                                                    'roomSketcher3D',
                                                                    'showRoomSketcher3D'
                                                                )
                                                            }
                                                            onUploadModalClose={() =>
                                                                this.onStoreyFilesUploadCompleted(
                                                                    roomSketcherPlan3D.deliveryFileId,
                                                                    'showRoomSketcher3D'
                                                                )
                                                            }
                                                            isAnnotation={
                                                                roomSketcherPlan3D.isAnnotation
                                                            }
                                                            uploadModalTitleSubject={
                                                                (storey.customStoreyName
                                                                    ? storey.customStoreyName
                                                                    : Translate(storey.storey)) +
                                                                ' (RoomSketcher 3D)'
                                                            }
                                                        />
                                                    </Col>
                                                )
                                            )}
                                    </div>
                                ))}
                                {this.props.planOrder.externalDeliveryFiles.map(
                                    (externalDeliveryFile, index) => (
                                        <Col md={3} key={'externalfiles-' + index}>
                                            <fieldset>
                                                <legend>{externalDeliveryFile.filename}</legend>
                                                {externalDeliveryFile.fileType ===
                                                    FileExtensions.unknown && (
                                                    <img className="pdf-icon" src={unknownIcon} />
                                                )}
                                                {externalDeliveryFile.fileType ===
                                                    FileExtensions.pdf && (
                                                    <img className="pdf-icon" src={pdfIcon} />
                                                )}
                                                {externalDeliveryFile.fileType ===
                                                    FileExtensions.psd && (
                                                    <img className="pdf-icon" src={psdIcon} />
                                                )}
                                                {externalDeliveryFile.fileType ===
                                                    FileExtensions.skp && (
                                                    <img className="pdf-icon" src={skpIcon} />
                                                )}
                                                {(externalDeliveryFile.fileType ===
                                                    FileExtensions.jpg ||
                                                    externalDeliveryFile.fileType ===
                                                        FileExtensions.png) && (
                                                    <ExternalImageDeliveryFile
                                                        thumbnailReference={
                                                            externalDeliveryFile.thumbnailReference
                                                        }
                                                        onClick={() =>
                                                            this.setState({
                                                                showImageModal: true,
                                                                selectedImage:
                                                                    externalDeliveryFile as IFile
                                                            } as IState)
                                                        }
                                                    />
                                                )}
                                                <br />
                                                <a
                                                    className="btn btn-default"
                                                    href={`${apiUrl}/file/${externalDeliveryFile.fileReference}`}
                                                    target="_blank"
                                                >
                                                    {Translate(
                                                        'order/plan/delivery/download-external-delivery-file/agent'
                                                    )}
                                                </a>
                                            </fieldset>
                                        </Col>
                                    )
                                )}
                            </Row>
                            {this.props.planOrder.externalAnnotationFiles.length > 0 && (
                                <Row>
                                    <Col xs={12}>
                                        <h2>
                                            {Translate(
                                                'plan-order/delivery/external-annotation-files-title'
                                            )}
                                        </h2>
                                    </Col>
                                    <div className="external-files-container">
                                        {this.props.planOrder.externalAnnotationFiles.map(
                                            (externalAnnotationFile, index) => (
                                                <Col
                                                    md={3}
                                                    key={'externalAnnotationFiles-' + index}
                                                >
                                                    <fieldset>
                                                        <legend>
                                                            {externalAnnotationFile.filename}
                                                        </legend>
                                                        {externalAnnotationFile.fileType ===
                                                            FileExtensions.unknown && (
                                                            <img
                                                                className="pdf-icon"
                                                                src={unknownIcon}
                                                            />
                                                        )}
                                                        {externalAnnotationFile.fileType ===
                                                            FileExtensions.pdf && (
                                                            <img
                                                                className="pdf-icon"
                                                                src={pdfIcon}
                                                            />
                                                        )}
                                                        {externalAnnotationFile.fileType ===
                                                            FileExtensions.psd && (
                                                            <img
                                                                className="pdf-icon"
                                                                src={psdIcon}
                                                            />
                                                        )}
                                                        {externalAnnotationFile.fileType ===
                                                            FileExtensions.skp && (
                                                            <img
                                                                className="pdf-icon"
                                                                src={skpIcon}
                                                            />
                                                        )}
                                                        {(externalAnnotationFile.fileType ===
                                                            FileExtensions.jpg ||
                                                            externalAnnotationFile.fileType ===
                                                                FileExtensions.png) && (
                                                            <div className="button-bar-below">
                                                                <div className="content-row">
                                                                    <ExternalImageDeliveryFile
                                                                        hideFilename={true}
                                                                        forAgent={true}
                                                                        readOnly={true}
                                                                        user={null}
                                                                        downloadTranslationKey={''}
                                                                        file={
                                                                            externalAnnotationFile as IExternalFile
                                                                        }
                                                                        onClick={() =>
                                                                            this.setState({
                                                                                showImageModal:
                                                                                    true,
                                                                                selectedImage:
                                                                                    externalAnnotationFile as IFile
                                                                            } as IState)
                                                                        }
                                                                    />
                                                                </div>
                                                                <ButtonToolbar>
                                                                    <ButtonGroup bsSize="sm">
                                                                        <a
                                                                            className="btn btn-default"
                                                                            href={`${apiUrl}/file/${externalAnnotationFile.fileReference}`}
                                                                            target="_blank"
                                                                        >
                                                                            {Translate(
                                                                                'order/plan/delivery/download-external-annotation-file/agent'
                                                                            )}
                                                                        </a>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={
                                                                                <Tooltip id="tooltip-annotate">
                                                                                    <p>
                                                                                        {Translate(
                                                                                            'order/plan/storey/annotate-button'
                                                                                        )}
                                                                                    </p>
                                                                                </Tooltip>
                                                                            }
                                                                        >
                                                                            <Button
                                                                                bsStyle="success"
                                                                                onClick={() =>
                                                                                    this.annotateExternalFile(
                                                                                        externalAnnotationFile.planOrderExternalFileId
                                                                                    )
                                                                                }
                                                                            >
                                                                                {Translate(
                                                                                    'order/plan/delivery/annotate'
                                                                                )}
                                                                            </Button>
                                                                        </OverlayTrigger>
                                                                        {Features.hasFeature(
                                                                            Features.DeleteExternalFile,
                                                                            this.props.user
                                                                        ) && (
                                                                            <Button
                                                                                bsStyle="danger"
                                                                                onClick={() =>
                                                                                    this.inActivateExternalFile(
                                                                                        externalAnnotationFile.planOrderExternalFileId
                                                                                    )
                                                                                }
                                                                            >
                                                                                {Translate(
                                                                                    'order/plan/delivery/external-annotation-file/remove'
                                                                                )}
                                                                            </Button>
                                                                        )}
                                                                    </ButtonGroup>
                                                                </ButtonToolbar>
                                                            </div>
                                                        )}
                                                        {externalAnnotationFile.fileType !==
                                                            FileExtensions.jpg &&
                                                            externalAnnotationFile.fileType !==
                                                                FileExtensions.png && (
                                                                <div>
                                                                    <br />
                                                                    <a
                                                                        className="btn btn-default"
                                                                        href={`${apiUrl}/file/${externalAnnotationFile.fileReference}`}
                                                                        target="_blank"
                                                                    >
                                                                        {Translate(
                                                                            'order/plan/delivery/download-external-annotation-file/agent'
                                                                        )}
                                                                    </a>
                                                                </div>
                                                            )}
                                                    </fieldset>
                                                </Col>
                                            )
                                        )}
                                    </div>
                                </Row>
                            )}
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('order/plan/customer-returned-message')}
                                </ControlLabel>
                                <FormControl
                                    componentClass="textarea"
                                    name="customerReturnedMessage"
                                    value={
                                        this.state.customerReturnedMessage
                                            ? this.state.customerReturnedMessage
                                            : ''
                                    }
                                    onChange={(input: any) =>
                                        this.setState({
                                            customerReturnedMessage: input.target.value
                                        } as IState)
                                    }
                                />
                            </FormGroup>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button onClick={this.downloadFiles}>
                                        {Translate('order/plan/delivery/download-files')}
                                    </Button>
                                    {Features.hasFeature(
                                        Features.ViewAdminPlanOrder,
                                        this.props.user
                                    ) && (
                                        <Button onClick={this.emailFiles}>
                                            {Translate('order/plan/delivery/email-files')}
                                        </Button>
                                    )}
                                </ButtonGroup>
                                <ButtonGroup>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id="tooltip-upload">
                                                <p>
                                                    {Translate(
                                                        'order/plan/delivery/upload-external-annotation-files'
                                                    )}
                                                </p>
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            bsStyle="info"
                                            onClick={() =>
                                                this.setState({
                                                    showUploadModal: true
                                                } as IState)
                                            }
                                        >
                                            <i className="material-icons no-label md-18">
                                                cloud_upload
                                            </i>
                                            &nbsp;&nbsp;
                                            {Translate('order/plan/delivery/upload')}
                                        </Button>
                                    </OverlayTrigger>
                                    <Button
                                        onClick={() =>
                                            this.setState({
                                                showReturnConfirmation: true
                                            } as IState)
                                        }
                                        bsStyle="danger"
                                    >
                                        {Translate('order/plan/delivery/return-button')}
                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>

                            {this.state.showAnnotationModal && this.props.annotation && (
                                <PlanAnnotationTool
                                    annotation={this.props.annotation}
                                    save={this.props.actions.saveDeliveryAnnotation}
                                    storeys={this.props.availableStories}
                                    type={this.state.annotationType}
                                    changeFile={(id: number) => this.showAnnotationModal(id)}
                                    close={() =>
                                        this.setState({
                                            showAnnotationModal: false,
                                            annotationType: null
                                        } as IState)
                                    }
                                    user={this.props.user}
                                    address={this.props.planOrder.addressLine1}
                                    byCustomer={true}
                                />
                            )}

                            {this.state.showImageModal && this.state.selectedImage && (
                                <ImageModal
                                    filename={this.state.selectedImage.filename}
                                    fileReference={this.state.selectedImage.fileReference}
                                    onClose={() => {
                                        this.setState({
                                            showImageModal: false,
                                            selectedImage: null
                                        } as IState);
                                    }}
                                />
                            )}

                            {this.state.showReturnConfirmation && (
                                <ConfirmationModal
                                    title={Translate(
                                        'order/plan/delivery/return/confirmation/title'
                                    )}
                                    text={Translate('order/plan/delivery/return/confirmation/text')}
                                    doButtonLabel={Translate(
                                        'order/plan/delivery/return/confirmation/do-button'
                                    )}
                                    cancelButtonLabel={Translate(
                                        'order/plan/delivery/return/confirmation/cancel-button'
                                    )}
                                    doButtonStyle="danger"
                                    do={this.return}
                                    closeModal={() =>
                                        this.setState({
                                            showReturnConfirmation: false
                                        } as IState)
                                    }
                                />
                            )}

                            {this.state.showUploadModal && (
                                <AnnotationFilesUploadModal
                                    title={Translate(
                                        'order/plan/delivery/upload-external-annotation-file-modal/title'
                                    )}
                                    dropzoneId={'agent-external-annotation-upload-modal'}
                                    actionUrl={`${apiUrl}/views/planorder/agent/order/${this.props.planOrder.id}`}
                                    upload={this.state.uploadExternalAnnotationFiles}
                                    onUploadCompleted={() =>
                                        this.setState({
                                            uploadExternalAnnotationFiles: false,
                                            showUploadModal: false
                                        } as IState)
                                    }
                                    onFileUploadCompleted={(response: any) =>
                                        this.onAnnotationFileUploadCompleted(
                                            response,
                                            this.props.actions.addPlanOrderAnnotationFile
                                        )
                                    }
                                    onUpload={() =>
                                        this.setState({
                                            uploadExternalAnnotationFiles: true
                                        } as IState)
                                    }
                                    closeModal={() =>
                                        this.setState({
                                            showUploadModal: false
                                        } as IState)
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    downloadFiles() {
        this.props.actions.downloadFiles(this.props.planOrder.id);
    }

    emailFiles() {
        this.props.actions
            .emailFiles(this.props.planOrder.id)
            .then(() => {
                notify.success(Translate('order/plan/delivery/email-success'));
            })
            .catch(() => {
                notify.failure(Translate('order/plan/delivery/email-error'));
            });
    }

    return() {
        this.props.actions
            .return(this.props.planOrder.id, this.state.customerReturnedMessage)
            .then(() => {
                notify.success(Translate('order/plan/delivery/return-success'));
            });
    }

    showAnnotationModal(id: number) {
        this.props.actions.editAnnotation(id).then(() => {
            this.setState({
                showAnnotationModal: true,
                annotationType: AnnotationNormal
            } as IState);
        });
    }

    annotateExternalFile(id: number) {
        this.props.actions.editExternalAnnotation(id).then(() => {
            this.setState({
                showAnnotationModal: true,
                annotationType: ExternalAnnotation
            } as IState);
        });
    }

    inActivateExternalFile(id: number) {
        this.props.actions
            .inActivateExternalFile(id)
            .then(() =>
                notify.success(Translate('order/plan/delivery/external-file-removed-success'))
            )
            .catch(handleError);
    }
}

function mapStateToProps(state: any, ownProps: IProps) {
    return {
        planOrder: ownProps.planOrder,
        annotation: state.viewPlanOrder.annotation,
        availableStories: state.viewPlanOrder.availableStories,
        user: state.user
    };
}

function mapDispatchToProps(dispatch: any): { actions: IActions } {
    return {
        actions: {
            downloadFiles: (planOrderId: number) =>
                dispatch(actions.downloadFiles(planOrderId, true)),
            emailFiles: (planOrderId: number) => dispatch(actions.emailFiles(planOrderId)),
            return: (planOrderId: number, customerReturnedMessage: string) =>
                dispatch(agentActions.returnPlanOrder(planOrderId, customerReturnedMessage)),
            editAnnotation: (id: number) => dispatch(actions.editAnnotation(id, AnnotationNormal)),
            editExternalAnnotation: (id: number) =>
                dispatch(actions.editExternalFileAnnotation(id)),
            saveDeliveryAnnotation: (
                planOrderStoreyFileId: number,
                data: string,
                revisionType: number,
                annotationType: string,
                hasDoneCrop: boolean,
                hasDoneFlip: boolean
            ) =>
                dispatch(
                    agentActions.saveAnnotation(
                        planOrderStoreyFileId,
                        data,
                        hasDoneCrop,
                        revisionType,
                        annotationType,
                        hasDoneFlip
                    )
                ),
            addPlanOrderStoreyAnnotationFile: (
                response: IUploadPlanOrderStoreyAnnotationFileResult
            ) => dispatch(agentActions.addPlanOrderStoreyAnnotationFile(response)),
            addPlanOrderAnnotationFile: (response: any) =>
                dispatch(agentActions.addPlanOrderAnnotationFile(response)),
            inActivateExternalFile: (id: number) => dispatch(actions.inActivateExternalFile(id))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeliveredPlanOrder);
